// Times, dates, sizes, durations and counts (02-building-blocks.md, "Times and dates", "Sizes,
// durations, counts", "Languages"): the browser's time zone, the words of the active language and
// its locale's names (Intl), the person's clock and date format (Settings → General, 21).
import { currentLanguage, locale, t } from '../i18n';

export interface Clock {
  /** Absent: the device's region's clock, else the language's (deviceH24). */
  h24?: boolean;
  /** "DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD", "YYYY-MM-DD" or a custom pattern; absent: the language's. */
  dateFormat?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const name = (d: Date, options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale(), options).format(d);
const h24 = (clock: Clock) => clock.h24 ?? deviceH24();

/** What the clock is read from: the browser's own formatting locale and its hour cycle, its languages, its time zone. */
export interface Device {
  locale: string;
  own24: boolean;
  languages: readonly string[];
  timeZone: string;
}
const is24 = (o: Intl.ResolvedDateTimeFormatOptions) => o.hourCycle === 'h23' || o.hourCycle === 'h24';
const hours24 = (locale: string) => is24(new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions());
const regionOf = (tag: string) => {
  try {
    return new Intl.Locale(tag).region;
  } catch {
    return undefined;
  }
};
/** A time zone's country, for a browser that names no region (02). */
const ZONES = ('Europe/Zurich CH Europe/Berlin DE Europe/Vienna AT Europe/Vaduz LI Europe/Luxembourg LU Europe/Paris FR ' +
  'Europe/Rome IT Europe/Madrid ES Europe/Lisbon PT Europe/Amsterdam NL Europe/Brussels BE Europe/London GB Europe/Dublin IE ' +
  'Europe/Copenhagen DK Europe/Stockholm SE Europe/Oslo NO Europe/Helsinki FI Europe/Warsaw PL Europe/Prague CZ ' +
  'Europe/Budapest HU Europe/Athens GR America/New_York US America/Chicago US America/Denver US America/Los_Angeles US ' +
  'America/Toronto CA America/Vancouver CA Australia/Sydney AU Australia/Melbourne AU Asia/Kolkata IN').split(' ');
const zoneRegion = (zone: string) => {
  const i = ZONES.indexOf(zone);
  return i % 2 === 0 ? ZONES[i + 1] : undefined;
};

/**
 * The device's region's clock (02, Times and dates, v1.58.24): the browser's own 12- or 24-hour
 * setting where its locale carries one, else the region's (of the browser's locale, else of the
 * first language naming one, else of the time zone's country); undefined with no region found.
 */
export function regionH24({ locale, own24, languages, timeZone }: Device): boolean | undefined {
  const base = locale.replace(/-u-.*/, '');
  if (own24 !== hours24(base)) return own24;
  const region = regionOf(base) ?? languages.map(regionOf).find(Boolean) ?? zoneRegion(timeZone);
  return region ? hours24(`en-${region}`) : undefined;
}

let region: { h24?: boolean } | undefined;
/** The clock while the person hasn't chosen one: the device's region's, else the language's (English 12-hour, German 24-hour). */
export function deviceH24(): boolean {
  region ??= (() => {
    const own = new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions();
    return { h24: regionH24({ locale: own.locale, own24: is24(own), languages: navigator.languages ?? [], timeZone: own.timeZone }) };
  })();
  return region.h24 ?? currentLanguage() === 'de';
}

/** A date in a pattern of D, DD, M, MM, MMM, MMMM, YY and YYYY; without one, the language's own. */
export function formatDate(d: Date, pattern?: string): string {
  if (!pattern)
    return name(d, currentLanguage() === 'de' ? { day: '2-digit', month: '2-digit', year: 'numeric' } : { day: 'numeric', month: 'short', year: 'numeric' });
  const parts: Record<string, string> = {
    YYYY: String(d.getFullYear()),
    YY: String(d.getFullYear()).slice(-2),
    MMMM: name(d, { month: 'long' }),
    MMM: name(d, { month: 'short' }),
    MM: pad(d.getMonth() + 1),
    M: String(d.getMonth() + 1),
    DD: pad(d.getDate()),
    D: String(d.getDate()),
  };
  return pattern.replace(/YYYY|YY|MMMM|MMM|MM|M|DD|D/g, (p) => parts[p]);
}

/** "4:10 PM" or "16:10". */
export function formatTime(d: Date, clock: Clock = {}): string {
  if (h24(clock)) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const h = d.getHours() % 12 || 12;
  return `${h}:${pad(d.getMinutes())} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
}

/** Whole calendar days from `d` to `now` (0 = today, 1 = yesterday). */
export function daysAgo(d: Date, now = new Date()): number {
  const day = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
  return Math.round((day(now) - day(d)) / 86_400_000);
}

/** "Today at 4:10 PM", "Yesterday at …", "Last Friday at …" (2–6 days), else "2 Oct 2026 4:10 PM". */
export function messageTime(d: Date, clock: Clock = {}, now = new Date()): string {
  const days = daysAgo(d, now);
  const time = formatTime(d, clock);
  if (days === 0) return t('time.todayAt', { time });
  if (days === 1) return t('time.yesterdayAt', { time });
  if (days > 1 && days < 7) return t('time.lastWeekdayAt', { weekday: name(d, { weekday: 'long' }), time });
  return `${formatDate(d, clock.dateFormat)} ${time}`;
}

/** A timeline's day line: "Today", "Yesterday", else "2 October 2026". */
export function dayLine(d: Date, now = new Date()): string {
  const days = daysAgo(d, now);
  if (days === 0) return t('time.today');
  if (days === 1) return t('time.yesterday');
  return name(d, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** In a list: "1:07 PM", "Yesterday", "2 Oct", "2 Oct 2025". */
export function listTime(d: Date, clock: Clock = {}, now = new Date()): string {
  const days = daysAgo(d, now);
  if (days === 0) return formatTime(d, clock);
  if (days === 1) return t('time.yesterday');
  const thisYear = d.getFullYear() === now.getFullYear();
  return name(d, thisYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "2 Oct 2026, 1:07 PM". */
export const dateWithTime = (d: Date, clock: Clock = {}) => t('time.dateTime', { date: formatDate(d, clock.dateFormat), time: formatTime(d, clock) });

/** An hour on its own: "1 PM" or "13:00". */
export function hourLabel(hour: number, clock: Clock = {}): string {
  if (h24(clock)) return `${pad(hour)}:00`;
  return `${hour % 12 || 12} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** "1.2 MB", "340.0 KB", "0 KB": decimal units, one decimal, at least KB. */
export function formatSize(bytes: number): string {
  if (bytes <= 0) return '0 KB';
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1000;
  let i = 0;
  while (value >= 1000 && i < units.length - 1) {
    value /= 1000;
    i++;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

/** "3:07". */
export const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${pad(Math.floor(seconds % 60))}`;

/** A badge's count: 1–99, above that "99+"; the real count goes in its accessible name (02). */
export const badgeCount = (n: number) => (n > 99 ? '99+' : String(n));

/** Compact numbers (member counts, 02). */
export function formatCount(n: number): string {
  if (n < 1000) return String(n);
  const thousands = Math.round(n / 100) / 10;
  return thousands < 1000 ? `${thousands}K` : `${Math.round(n / 100_000) / 10}M`;
}

/** The ID colour (1–8): a 32-bit string hash of the ID (02, Avatars). */
export function idColour(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (Math.imul(hash, 31) + id.charCodeAt(i)) | 0;
  return (Math.abs(hash) % 8) + 1;
}

/** The avatar's letter: the name's first character, capitalised; "\uFFFD" without a name. Spaces on
 * the rail take two (07): the first two characters, the second as written; a space there ends it. */
export function initial(name: string | undefined, letters = 1): string {
  const [first, second] = [...new Intl.Segmenter().segment(name?.trim() ?? '')].map((g) => g.segment);
  if (!first) return '\uFFFD';
  return first.toLocaleUpperCase() + (letters > 1 && second && /\S/.test(second) ? second : '');
}
